import { useCallback } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import type { TaskCreate } from "@patent-studio/api-types";
import { useCreateTask } from "@/hooks/use-tasks";
import { ApiError, refusalDetail } from "@/services/api-client";
import { stagePath } from "../stage-vocabulary";
import type { ErrorCode } from "@/lib/problem/error-words";

/** What the attorney gives a draft before it exists: everything it is made with but its client. */
type NewDraftDetails = Pick<
  TaskCreate,
  "title" | "inventors" | "jurisdiction" | "filing_deadline"
>;

const TRY_AGAIN = "Could not create the draft. Please try again.";

/** Typed by the API's own list, so a renamed code fails the build rather than never matching. */
const ALLOWANCE_USED: ErrorCode = "DRAFT_ALLOWANCE_USED";

/**
 * A pilot registrant out of drafts is told why in the server's words, since
 * trying again would only be refused again; any other failure asks for a retry.
 */
function whyNotCreated(error: unknown): string {
  const allowanceUsed =
    error instanceof ApiError && error.code === ALLOWANCE_USED;
  return allowanceUsed ? refusalDetail(error, TRY_AGAIN) : TRY_AGAIN;
}

/**
 * Starts a draft under a client with the details it is made with, and opens it
 * at Intake once it exists.
 *
 * The draft is created server-side before the page moves, so a route mounting
 * never writes a row. A failure is toasted and rethrown, so the caller stays
 * where it is and the retry the toast asks for is reachable.
 */
export function useStartDraft() {
  const navigate = useNavigate();
  const { mutateAsync: createTask, isPending } = useCreateTask();
  const start = useCallback(
    async (clientId: string, details: NewDraftDetails) => {
      let created;
      try {
        created = await createTask({ ...details, client_id: clientId });
      } catch (error) {
        toast.error(whyNotCreated(error));
        throw error;
      }
      void navigate(stagePath(created.task_id, "intake"));
    },
    [createTask, navigate],
  );
  return { start, starting: isPending };
}
